import { useState, lazy, Suspense } from "react";
import { useQuery, useMutation } from "convex/react";
import { Capacitor } from "@capacitor/core";
import { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { Map as MapIcon, Image, Loader2, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppleMap from "@/plugins/apple-map";
import { cn } from "@/lib/utils";

// Lazy load RouteMap (includes leaflet ~120KB)
const RouteMap = lazy(() => import("./route-map").then(m => ({ default: m.RouteMap })));

type RouteTab = "map" | "screenshot";

interface RouteSectionProps {
  shiftId?: Id<"shifts">;
  shiftStatus?: string;
  screenshotUrl?: string | null;
  /** Hide the native map (e.g., when a sheet is open) */
  hidden?: boolean;
}

export function RouteSection({
  shiftId,
  shiftStatus,
  screenshotUrl,
  hidden = false,
}: RouteSectionProps) {
  const hasTrackedRoute = shiftId && shiftStatus === "completed";
  const hasScreenshot = !!screenshotUrl;

  const [activeTab, setActiveTab] = useState<RouteTab>(
    hasTrackedRoute ? "map" : "screenshot"
  );
  const [isOpening, setIsOpening] = useState(false);
  const [mapRefreshTrigger, setMapRefreshTrigger] = useState(0);

  // Query location points for expand button
  const locationPoints = useQuery(
    api.shifts.getLocationPoints,
    shiftId ? { shiftId } : "skip"
  );
  const shift = useQuery(api.shifts.getShift, shiftId ? { id: shiftId } : "skip");

  const isIOS = Capacitor.getPlatform() === "ios";
  const updateMiles = useMutation(api.shifts.updateAdjustedMiles);

  // Handle opening in Apple Maps with adjustment mode
  const handleOpenInMaps = async () => {
    if (!locationPoints || locationPoints.length < 2 || !shiftId) return;

    setIsOpening(true);
    try {
      const result = await AppleMap.showRoute({
        points: locationPoints.map((p) => ({ lat: p.lat, lng: p.lng })),
        title: shift?.type === "commute" ? "Commute Route" : "Shift Route",
        totalMiles: shift?.totalMiles ?? 0,
        adjustable: true, // Enable adjustment mode
      });

      // If user adjusted and saved, persist to database
      if (result.wasAdjusted && result.adjustedMiles !== undefined) {
        await updateMiles({
          shiftId: shiftId,
          adjustedMiles: result.adjustedMiles,
          startIndex: result.startIndex,
          endIndex: result.endIndex,
        });
      }

      // Trigger inline map to refresh visibility after modal closes
      setMapRefreshTrigger((prev) => prev + 1);
    } catch (error: unknown) {
      console.error("[AppleMap] Failed:", JSON.stringify(error));
    } finally {
      setIsOpening(false);
    }
  };

  // Show expand button on iOS with valid route data
  const canExpand = isIOS && hasTrackedRoute && locationPoints && locationPoints.length >= 2;

  // Count available tabs
  const tabs: { id: RouteTab; label: string; icon: typeof MapIcon; available: boolean }[] = [
    { id: "map", label: "Map", icon: MapIcon, available: !!hasTrackedRoute },
    { id: "screenshot", label: "Screenshot", icon: Image, available: hasScreenshot },
  ];

  const availableTabs = tabs.filter((t) => t.available);

  // If no route data at all
  if (availableTabs.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MapIcon className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">Route</span>
        </div>
        <div className="h-[150px] bg-muted/50 rounded-lg flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <MapIcon className="h-8 w-8" />
          <p className="text-sm">No route data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapIcon className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">Route</span>
        </div>

        {/* Tab buttons and expand button */}
        <div className="flex items-center gap-1">
          {availableTabs.length > 1 && availableTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors",
                activeTab === tab.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              <tab.icon className="h-3 w-3" />
              {tab.label}
            </button>
          ))}
          {canExpand && activeTab === "map" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleOpenInMaps}
              disabled={isOpening}
            >
              <Maximize2 className="h-3.5 w-3.5 mr-1" />
              {isOpening ? "Opening..." : "Expand"}
            </Button>
          )}
        </div>
      </div>

      {/* Tab content */}
      <div className="min-h-[200px]">
        {activeTab === "map" && hasTrackedRoute && shiftId && (
          <Suspense fallback={
            <div className="flex h-[200px] items-center justify-center rounded-lg border bg-muted/50">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          }>
            <RouteMap shiftId={shiftId} refreshTrigger={mapRefreshTrigger} hidden={hidden} />
          </Suspense>
        )}
        {activeTab === "screenshot" && screenshotUrl && (
          <img
            src={screenshotUrl}
            alt="Route screenshot"
            className="w-full rounded-lg border"
          />
        )}
      </div>
    </div>
  );
}
